import React from 'react';
import Modal from '../modal/Modal';
import { BulkChangeCoachResult, ChangeCoachFailure } from '../../lib/changeCoachError';
import ChangeCoachFailureCard, { ChangeCoachInquiryInfo } from './ChangeCoachFailureCard';

type ResultProps = {
    result: BulkChangeCoachResult;
    recoveryNotice?: string;
    onSelectFailed: () => void;
    onClose: () => void;
};

const actionButtonStyle = (primary: boolean): React.CSSProperties => ({
    height: 44,
    padding: '0 20px',
    borderRadius: 8,
    border: `1px solid ${primary ? '#2563EB' : '#D1D5DB'}`,
    backgroundColor: primary ? '#2563EB' : '#FFFFFF',
    color: primary ? '#FFFFFF' : '#374151',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
});

const BulkChangeCoachResultModal = ({ result, recoveryNotice, onSelectFailed, onClose }: ResultProps) => (
    <Modal
        whiteViewWidth={720}
        whiteViewHeight={620}
        isCloseButton
        onClose={onClose}
        isCloseOutside
    >
        <div style={{ minHeight: '100%', padding: '34px 40px 30px' }}>
            <h2 style={{ margin: 0, color: '#111827', fontSize: 22, lineHeight: '30px', fontWeight: 800 }}>
                담당 강사 변경 결과
            </h2>
            <p style={{ margin: '8px 0 0', color: '#6B7280', fontSize: 14, lineHeight: '21px' }}>
                처리 결과를 확인하고, 실패한 회원만 다시 선택할 수 있어요.
            </p>

            <div
                style={{
                    display: 'flex',
                    gap: 10,
                    marginTop: 22,
                    padding: '15px 18px',
                    borderRadius: 12,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#F8FAFC',
                }}
            >
                <span
                    style={{
                        padding: '6px 12px',
                        borderRadius: 999,
                        backgroundColor: '#ECFDF5',
                        color: '#047857',
                        fontSize: 14,
                        fontWeight: 800,
                    }}
                >
                    성공 {result.successCount}명
                </span>
                <span
                    style={{
                        padding: '6px 12px',
                        borderRadius: 999,
                        backgroundColor: '#FEF2F2',
                        color: '#B91C1C',
                        fontSize: 14,
                        fontWeight: 800,
                    }}
                >
                    실패 {result.failureCount}명
                </span>
            </div>

            <section style={{ marginTop: 20 }} aria-labelledby="bulk-change-failed-title">
                <h3
                    id="bulk-change-failed-title"
                    style={{ margin: '0 0 10px', color: '#111827', fontSize: 15, fontWeight: 800 }}
                >
                    확인이 필요한 회원
                </h3>
                <div
                    style={{
                        maxHeight: 210,
                        overflowY: 'auto',
                        borderRadius: 10,
                        border: '1px solid #FECACA',
                        backgroundColor: '#FFFFFF',
                    }}
                >
                    {result.failed.map((item, index) => (
                        <div
                            key={item.memberId || `${item.name}-${index}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '130px minmax(0, 1fr)',
                                gap: 14,
                                padding: '13px 16px',
                                borderBottom: index < result.failed.length - 1 ? '1px solid #FEE2E2' : 'none',
                            }}
                        >
                            <strong style={{ color: '#111827', fontSize: 14, lineHeight: '21px' }}>{item.name}</strong>
                            <span style={{ color: '#4B5563', fontSize: 13, lineHeight: '20px' }}>{item.reason}</span>
                        </div>
                    ))}
                    {result.failureCount > result.failed.length && (
                        <div style={{ padding: '12px 16px', color: '#6B7280', fontSize: 13, lineHeight: '20px' }}>
                            외 {result.failureCount - result.failed.length}명은 사유를 받지 못했어요. 회원 목록에서 담당 강사를
                            확인해 주세요.
                        </div>
                    )}
                </div>
            </section>

            {result.successCount > 0 && (
                <details style={{ marginTop: 14 }}>
                    <summary style={{ color: '#4B5563', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                        성공한 회원 {result.successCount}명 보기
                    </summary>
                    <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 8, backgroundColor: '#F0FDF4' }}>
                        {result.succeeded.length > 0 ? (
                            <span style={{ color: '#166534', fontSize: 13, lineHeight: '20px' }}>
                                {result.succeeded.map((item) => item.name).join(', ')}
                            </span>
                        ) : (
                            <span style={{ color: '#166534', fontSize: 13 }}>성공한 회원은 최신 목록에서 확인해 주세요.</span>
                        )}
                    </div>
                </details>
            )}

            {recoveryNotice && (
                <p
                    style={{
                        margin: '14px 0 0',
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: '#FFF7ED',
                        color: '#9A3412',
                        whiteSpace: 'pre-line',
                        fontSize: 13,
                        lineHeight: '20px',
                    }}
                >
                    {recoveryNotice.trim()}
                </p>
            )}

            <div
                style={{
                    display: 'flex',
                    gap: 10,
                    marginTop: 24,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                }}
            >
                <button type="button" style={actionButtonStyle(false)} onClick={onClose}>
                    닫기
                </button>
                <button type="button" style={actionButtonStyle(true)} onClick={onSelectFailed}>
                    실패한 회원만 다시 선택
                </button>
            </div>
        </div>
    </Modal>
);

type FailureProps = {
    failure: ChangeCoachFailure;
    isRetrying?: boolean;
    inquiryInfo?: ChangeCoachInquiryInfo;
    onRetry?: () => void;
    onRefresh?: () => void;
    onClose: () => void;
};

export const BulkChangeCoachFailureModal = ({
    failure,
    isRetrying,
    inquiryInfo,
    onRetry,
    onRefresh,
    onClose,
}: FailureProps) => (
    <Modal
        whiteViewWidth={720}
        whiteViewHeight={430}
        isCloseButton
        onClose={onClose}
        isCloseOutside={!isRetrying}
    >
        <div style={{ padding: '34px 40px' }}>
            <h2 style={{ margin: 0, color: '#111827', fontSize: 22, lineHeight: '30px', fontWeight: 800 }}>
                담당 강사 일괄 변경
            </h2>
            <p style={{ margin: '8px 0 20px', color: '#6B7280', fontSize: 14, lineHeight: '21px' }}>
                선택한 회원의 변경 요청을 완료하지 못했어요.
            </p>
            <ChangeCoachFailureCard
                failure={failure}
                isRetrying={isRetrying}
                inquiryInfo={inquiryInfo}
                onRetry={onRetry}
                onRefresh={onRefresh}
            />
        </div>
    </Modal>
);

export default BulkChangeCoachResultModal;
